<template>
 <div class="bg-page-bg dark:bg-page-bg-dark transition-colors duration-200" :class="(isWide || mobileFillTab) ? 'h-dvh overflow-hidden flex flex-col' : 'min-h-dvh'">
 <AppHeader
 title="Dashboard"
 :current-user="currentUser"
 show-help
 @help="startTourManually()"
 @preferences-saved="hideExcusedLessons = $event"
 @logout="handleLogout"
 />

 <!-- Main Content -->
 <main class="px-2 w-full" :class="isWide ? 'flex-1 min-h-0 flex flex-col overflow-hidden py-2' : mobileFillTab ? 'flex-1 min-h-0 flex flex-col overflow-hidden py-2 pb-20' : 'py-6 pb-20'">
 <div class="animate-fade-in w-full" :class="isWide ? 'h-full min-h-0 flex flex-col mx-auto max-w-[3000px]' : mobileFillTab ? 'h-full min-h-0 flex flex-col' : 'mx-auto max-w-[500px]'">

 <!-- WIDE: CSS Grid, not flexbox — a `1fr` track is guaranteed to be exactly
 "whatever's left" after the fixed-px tracks, with no content-based override.
 Flexbox's automatic-minimum-size behavior (content can refuse to shrink a
 flex-grow/shrink item below its own min-content size) kept causing exactly the
 kind of "won't resize / leaves a gap" bugs Grid's track model doesn't have. -->
 <div
 ref="rowEl"
 :class="isWide ? 'flex-1 min-h-0 w-full grid' : mobileFillTab ? 'flex-1 min-h-0 flex flex-col' : ''"
 :style="isWide ? { gridTemplateColumns: `${leftColWidth}px ${VERTICAL_HANDLE_WIDTH}px 1fr` } : {}"
 >

 <!-- Stats+Calendar column. Stats/Calendar height split is user-resizable. -->
 <div v-if="isWide" class="h-full w-full min-h-0 min-w-0 flex flex-col">
 <div class="flex-1 min-h-0 mb-2">
 <!-- Signups/admin users get the all-teachers team calendar here instead of their own schedule -->
 <component v-if="hasSignupsPermission" :is="teamCalendarViews[teamCalendarMode]" hide-tab-switcher :height="calendarGridHeight">
 <template #extra-controls>
 <CalendarModePicker v-model="teamCalendarMode" :options="TEAM_CALENDAR_MODES" />
 </template>
 </component>
 <template v-else>
 <MonthView v-if="myCalendarMode === 'month'" show-requests lesson-endpoint="/api/lessons/week" :locked-teacher="previewName" :height="calendarGridHeight" :hide-excused="hideExcusedLessons"
 @open-day="iso => { myCalendarDate = iso; myCalendarMode = 'week' }">
 <template #extra-controls><CalendarModePicker v-model="myCalendarMode" :options="MY_CALENDAR_MODES" /></template>
 </MonthView>
 <DayView v-else :key="myCalendarDate" show-requests lesson-endpoint="/api/lessons/week" :locked-teacher="previewName" :height="calendarGridHeight" :hide-excused="hideExcusedLessons" :initial-date="myCalendarDate" hide-tab-switcher>
 <template #extra-controls><CalendarModePicker v-model="myCalendarMode" :options="MY_CALENDAR_MODES" /></template>
 </DayView>
 </template>
 </div>

 <!-- Resize handle: Calendar ↔ Stats (height only) -->
 <div data-tour="resize-handle-vertical" class="h-3 flex-shrink-0 cursor-row-resize group flex items-center justify-center" @mousedown="startHeightResize" @dblclick="statsCollapsed = !statsCollapsed">
 <div data-tour="resize-grip-vertical" class="w-10 h-1 rounded-full bg-stroke dark:bg-stroke-dark group-hover:bg-primary-400 dark:group-hover:bg-primary-500 transition-colors"></div>
 </div>

 <div data-tour="stats-pane" class="flex-shrink-0 overflow-hidden flex flex-col" :style="{ height: statsCollapsed ? 'auto' : statsHeight + 'px' }">
 <div data-tour="stats-pane-tabs" :class="['flex items-center gap-0 border-b border-stroke dark:border-stroke-dark flex-shrink-0', !statsCollapsed && 'mb-2']">
 <button
 @click="selectStatsTab('lessons')"
 :class="['px-4 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors', statsPaneTab === 'lessons' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-fg-muted dark:text-fg-muted-dark']"
 >Lessons</button>
 <button
 @click="selectStatsTab('invoices')"
 data-tour="stats-tab-invoices"
 :class="['px-4 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors', statsPaneTab === 'invoices' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-fg-muted dark:text-fg-muted-dark']"
 ><span class="inline-flex items-center gap-1.5">Invoices<span v-if="badges.invoices" class="min-w-[1rem] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 font-medium text-center">{{ formatBadge(badges.invoices) }}</span></span></button>
 <button
 @click="selectStatsTab('students')"
 data-tour="stats-tab-students"
 :class="['px-4 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors', statsPaneTab === 'students' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-fg-muted dark:text-fg-muted-dark']"
 >Students</button>
 <button
 @click="statsCollapsed = !statsCollapsed"
 :title="statsCollapsed ? 'Expand panel' : 'Minimize panel'"
 class="ml-auto p-1 rounded text-fg-muted dark:text-fg-muted-dark hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
 >
 <ChevronDownIcon :class="['w-4 h-4 transition-transform', statsCollapsed && 'rotate-180']" />
 </button>
 </div>
 <div v-show="!statsCollapsed" class="flex-1 min-h-0 overflow-hidden">
 <StatsTab v-if="statsPaneTab === 'invoices'" :max-height="statsHeight + 'px'" contained />
 <MyLessonsListView v-else-if="statsPaneTab === 'lessons'" />
 <StudentsTab v-else-if="statsPaneTab === 'students' && currentUser" />
 </div>
 </div>
 </div>

 <!-- Resize handle: left column ↔ Attendance (width only) -->
 <div v-if="isWide" data-tour="resize-handle-horizontal" class="h-full cursor-col-resize group flex items-center justify-center" @mousedown="startWidthResize">
 <div data-tour="resize-grip-horizontal" class="w-1 h-10 rounded-full bg-stroke dark:bg-stroke-dark group-hover:bg-primary-400 dark:group-hover:bg-primary-500 transition-colors"></div>
 </div>

 <!-- WIDE: the workspace sidebar — the grid's `1fr` track, height-matched to the
 Stats+Calendar column. Its Attendance and Signups tabs are pinned here; contacts and
 forms opened from anywhere join them, and the same tabs follow you to the admin
 pages. WorkspaceSidebar (AppRoot.vue) is teleported in — see useAdminSidebar.js. -->
 <div v-if="isWide" id="workspace-sidebar-dashboard" class="h-full w-full min-h-0 min-w-0 flex flex-col"></div>

 <!-- MOBILE: single-column tab content. My Schedule alone fills the remaining
 flex height (see mobileFillTab) so the calendar's own scroll container is
 the only scrollable region, instead of also scrolling with the page. -->
 <template v-if="!isWide">
 <AttendanceTab v-if="activeTab === 'attendance' && currentUser" :teacher-name="previewName" class="mb-8" />
 <StudentCheckIns v-if="activeTab === 'lesson-signups' && currentUser" />
 <LessonSignupsTab v-if="activeTab === 'lesson-signups' && currentUser && hasSignupsPermission" class="mb-8" :is-admin="effectivePermissions.includes('admin')" />
 <TeacherSignupsTab v-else-if="activeTab === 'lesson-signups' && currentUser && showSignupsTab" class="mb-8" />
 <StatsTab v-if="activeTab === 'month-stats'" class="mb-8" />
 <div v-if="activeTab === 'my-schedule' && currentUser" class="flex-1 min-h-0">
 <DayView show-requests lesson-endpoint="/api/lessons/week" :locked-teacher="previewName" :hide-excused="hideExcusedLessons" hide-tab-switcher center-nav no-auto-fit height="100%" />
 </div>
 <StudentsTab v-if="activeTab === 'students' && currentUser" class="mb-8" />
 </template>

 </div>
 </div>
 </main>

 <!-- First-login password setup — overlays the real dashboard (already fetching/
 rendering behind the dim scrim) instead of a blank form, and needs no separate
 login step afterward since the invite flow already set the auth cookie. -->
 <SetPasswordModal
 v-if="currentUser?.needs_password"
 :name="previewName"
 :email="currentUser.email"
 @done="currentUser.needs_password = false; maybeStartFirstRunTour()"
 />

 <!-- Bottom Navigation (hidden on wide screens) -->
 <div v-if="!isWide" class="fixed bottom-0 left-0 right-0 z-50 bg-header-bg dark:bg-header-bg-dark border-t border-header-border dark:border-header-border-dark">
 <div class="flex py-1">
 <button
 v-for="tab in mobileTabs"
 :key="tab.id"
 :data-tour="'nav-' + tab.id"
 @click="router.replace(tab.path)"
 :class="[
 'flex flex-col flex-1 items-center p-2 rounded-lg touch-target',
 activeTab === tab.id
 ? 'text-primary-600 dark:text-primary-400'
 : 'text-fg-muted dark:text-fg-muted-dark'
 ]"
 >
 <span class="relative">
 <component :is="tab.icon" class="h-6 w-6" />
 <span v-if="tab.badge" class="min-w-[1rem] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 font-medium text-center absolute -top-1.5 -right-2.5">{{ formatBadge(tab.badge) }}</span>
 </span>
 <span class="text-xs mt-1">{{ tab.label }}</span>
 </button>
 </div>
 </div>
 </div>
</template>

<script setup>
defineOptions({ name: 'Dashboard' })
import { ref, onMounted, computed, watch, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useApi } from './composables/useApi.js'
import { useTenantBranding } from './composables/useTenantBranding.js'
import {
 ChartBarIcon,
 CalendarDaysIcon,
 CalendarIcon,
 UserPlusIcon,
 UserGroupIcon,
 ChevronDownIcon,
} from '@heroicons/vue/24/outline'
import AttendanceTab from './components/tabs/AttendanceTab.vue'
import StudentCheckIns from './components/common/StudentCheckIns.vue'
import LessonSignupsTab from './components/tabs/LessonSignupsTab.vue'
import TeacherSignupsTab from './components/tabs/TeacherSignupsTab.vue'
import StatsTab from './components/tabs/StatsTab.vue'
import StudentsTab from './components/tabs/StudentsTab.vue'
import MyLessonsListView from './components/tabs/MyLessonsListView.vue'
import DayView from './components/admin/views/DayView.vue'
import WeekView from './components/admin/views/WeekView.vue'
import MonthView from './components/admin/views/MonthView.vue'
import CalendarModePicker from './components/calendar/CalendarModePicker.vue'
import AppHeader from './components/common/AppHeader.vue'
import SetPasswordModal from './components/SetPasswordModal.vue'
import { useSync } from './composables/useSync'
import { useDemoMode } from './composables/useDemoMode'
import { useViewport } from './composables/useViewport'
import { useTour } from './composables/useTour'
import { TOUR_VERSION } from './tours/tourSteps'
import { registerCalendarModeHost } from './composables/useActiveCalendarMode'
import { useActiveSearchFocus } from './composables/useActiveSearchFocus'
import { useAdminSidebar, ATTENDANCE } from './composables/useAdminSidebar.js'
import { useTeacherBadges, formatBadge } from './composables/useTeacherBadges.js'

const router = useRouter()
const route = useRoute()
const { acronym } = useTenantBranding()

// Router's afterEach sets document.title with whatever acronym is loaded at
// navigation time; branding fetches async, so re-apply once it resolves in
// case the first navigation already fired with the 'BMA' placeholder.
watch(acronym, (value) => {
  const [prefix, suffix] = document.title.split(' | ')
  document.title = suffix ? `${prefix} | ${value}` : value
})
const { fetchApi } = useApi()
const { startTour, abortIfSurfaceMismatch } = useTour(router)

// Reactive state
const currentUser = ref(null)
// While previewing a teacher, the app should look and behave like that
// teacher's own session — their name in the header, their permissions
// gating admin-only controls — not the real logged-in admin's.
const previewName = useDemoMode().previewNameFor(() => currentUser.value?.name)
const effectivePermissions = useDemoMode().effectivePermissionsFor(() => currentUser.value?.permissions)
const activeTab = computed(() => {
 if (route.path === '/signups') return 'lesson-signups'
 if (route.path === '/stats') return 'month-stats'
 if (route.path === '/my-schedule') return 'my-schedule'
 if (route.path === '/students') return 'students'
 return 'attendance'
})
const { isWide, viewportWidth, release: releaseViewport } = useViewport()
// My Schedule is the one mobile tab whose content (the calendar) manages its own
// internal scrolling — letting the page ALSO scroll around it creates a scroll-
// within-scroll. So this tab alone locks the page to viewport height (same technique
// as the wide layout) instead of the normal free-scrolling mobile page.
const mobileFillTab = computed(() => !isWide.value && activeTab.value === 'my-schedule')
const statsPaneTab = ref(localStorage.getItem('statsPaneTab') || 'lessons')
watch(statsPaneTab, (val) => localStorage.setItem('statsPaneTab', val))
// Minimized = tab bar only; statsHeight is left untouched so expanding restores it.
const statsCollapsed = ref(localStorage.getItem('statsPaneCollapsed') === '1')
watch(statsCollapsed, (val) => localStorage.setItem('statsPaneCollapsed', val ? '1' : '0'))
// Clicking the active tab toggles minimize; any tab click while minimized expands to it.
const selectStatsTab = (tab) => {
 if (statsCollapsed.value) statsCollapsed.value = false
 else if (statsPaneTab.value === tab) statsCollapsed.value = true
 statsPaneTab.value = tab
}

// ── Stats/Calendar/Attendance layout (wide screens) ─────────────────────────
// <main> is a fixed-height flex column (h-screen shell) so its content row's height
// is measured directly via ResizeObserver rather than approximated from window size —
// that measured height drives the left column so Calendar/Attendance fill it exactly.
// Stats + Calendar scroll internally; Attendance uses AttendanceTab's `contained` mode
// (redirects its normally window-based auto-scroll/infinite-scroll to its own container).
// Both the Stats/Calendar height split and the Calendar/Attendance width split are
// user-resizable (drag handles below).
const CALENDAR_BOTTOM_MARGIN = 8  // mb-2 below the calendar, matching main's py-2 gap above
const HORIZONTAL_HANDLE_HEIGHT = 12
const MIN_STATS_HEIGHT = 120
const MIN_CALENDAR_HEIGHT = 260
const MIN_LEFT_WIDTH = 280
const MAX_LEFT_WIDTH = 2720
const MIN_ATTENDANCE_WIDTH = 360  // narrow floor — just enough for a lesson card to stay legible
const MAX_ATTENDANCE_WIDTH = 500  // wide ceiling — no reason for a lesson-card list to get much wider
const CONTAINER_MAX_WIDTH = 3000  // keep in sync with the max-w-[3000px] on the content wrapper
const CONTAINER_H_PADDING = 16    // px-2 on <main>, both sides
const VERTICAL_HANDLE_WIDTH = 16

const leftColWidth = ref(Number(localStorage.getItem('teacherLeftColWidth')) || 1050)
const statsHeight = ref(Number(localStorage.getItem('teacherStatsHeight')) || 220)

const rowEl = ref(null)
const rowHeight = ref(Math.max(300, window.innerHeight - 150))  // reasonable guess until ResizeObserver measures the real value
let rowResizeObserver = null

const leftColHeight = computed(() => Math.max(300, rowHeight.value))

// The calendar's wrapping div (flex-1 min-h-0, see template) is already sized exactly by
// flexbox — no JS needed to compute its pixel height. Passing '100%' lets DayView's own
// height:100% just resolve against that flex-computed size directly, so it can never drift
// from the real layout (a previous version computed this in JS by subtracting estimated
// constants from leftColHeight, which drifted from the real rendered layout by a few px —
// enough for the calendar to overflow its flex column and get clipped by <main>'s
// overflow-hidden, cutting off the rounded bottom corner and the margin below it).
const calendarGridHeight = '100%'

const TEAM_CALENDAR_MODES = [
 { value: 'day', label: 'Week' }, // DayView, labelled Week — the real WeekView is disabled (see router/index.js)
 // { value: 'week', label: 'Week' },
 { value: 'month', label: 'Month' },
]
const teamCalendarViews = { day: DayView, week: WeekView, month: MonthView }
const teamCalendarMode = ref('day')

// Teacher's own (locked) calendar: just two modes. "Week" is really DayView — its rolling
// multi-day window already reads as a week for a single teacher's column.
const MY_CALENDAR_MODES = [
 { value: 'week', label: 'Week' },
 { value: 'month', label: 'Month' },
]
const myCalendarMode = ref('week')
const myCalendarDate = ref(null) // set when Month view clicks through to a specific day

// "s" shortcut (AppRoot.vue): on the Dashboard it always means the Students
// search, switching to that tab first, not whichever toolbar happens to be up.
const activeSearchFocus = useActiveSearchFocus()
const studentsSearchOverride = {
 label: 'Search students',
 focus: async () => {
  if (isWide.value) { statsPaneTab.value = 'students'; statsCollapsed.value = false }
  else await router.push('/students')
  await nextTick()
  activeSearchFocus.focusNamed('students')
 },
}
onMounted(() => activeSearchFocus.setOverride(studentsSearchOverride))
onUnmounted(() => activeSearchFocus.clearOverride(studentsSearchOverride))

// "1"/"2" shortcuts (AppRoot.vue). The teacher calendar's "week" mode is DayView.
registerCalendarModeHost(mode => {
 if (hasSignupsPermission.value) teamCalendarMode.value = mode
 else myCalendarMode.value = mode === 'day' ? 'week' : 'month'
}, () => isWide.value)

// Dynamic ceiling on statsHeight so Calendar never gets squeezed below its floor.
const maxStatsHeight = computed(() =>
 Math.max(MIN_STATS_HEIGHT, leftColHeight.value - MIN_CALENDAR_HEIGHT - HORIZONTAL_HANDLE_HEIGHT - CALENDAR_BOTTOM_MARGIN)
)
watch(maxStatsHeight, (max) => {
 if (statsHeight.value > max) statsHeight.value = max
})
if (statsHeight.value > maxStatsHeight.value) statsHeight.value = maxStatsHeight.value

// Dynamic ceiling on the left column so Attendance never gets squeezed below its floor,
// even on a viewport too small for the static MAX_LEFT_WIDTH to make sense.
const dynamicMaxLeftWidth = computed(() => {
 const containerWidth = Math.min(viewportWidth.value - CONTAINER_H_PADDING, CONTAINER_MAX_WIDTH)
 return Math.max(MIN_LEFT_WIDTH, containerWidth - MIN_ATTENDANCE_WIDTH - VERTICAL_HANDLE_WIDTH)
})
watch(dynamicMaxLeftWidth, (max) => {
 if (leftColWidth.value > max) leftColWidth.value = max
})
if (leftColWidth.value > dynamicMaxLeftWidth.value) leftColWidth.value = dynamicMaxLeftWidth.value

// Dynamic floor on the left column so Attendance never exceeds MAX_ATTENDANCE_WIDTH —
// the mirror image of dynamicMaxLeftWidth above. Once the drag hits this floor it just
// stops (clamped, like every other limit here); it does NOT shrink the whole row and
// leave blank margin the way an unclamped drag used to.
const dynamicMinLeftWidth = computed(() => {
 const containerWidth = Math.min(viewportWidth.value - CONTAINER_H_PADDING, CONTAINER_MAX_WIDTH)
 return Math.max(MIN_LEFT_WIDTH, containerWidth - MAX_ATTENDANCE_WIDTH - VERTICAL_HANDLE_WIDTH)
})
watch(dynamicMinLeftWidth, (min) => {
 if (leftColWidth.value < min) leftColWidth.value = min
})
if (leftColWidth.value < dynamicMinLeftWidth.value) leftColWidth.value = dynamicMinLeftWidth.value

// Absolute-position tracking (target = mouse X minus the row's left edge), not a
// delta from drag-start — a delta-based approach means once a clamp (MIN_LEFT_WIDTH /
// the dynamic ceiling) kicks in, the start reference goes stale, creating a "dead
// zone" where the handle stops responding until the mouse re-crosses that same
// distance. Absolute tracking has no such dead zone: the target position is always
// computed fresh from where the mouse actually is.
let rowLeftEdge = 0

const onWidthResize = (e) => {
 e.preventDefault()
 const ceiling = Math.min(MAX_LEFT_WIDTH, dynamicMaxLeftWidth.value)
 const floor = dynamicMinLeftWidth.value
 leftColWidth.value = Math.min(ceiling, Math.max(floor, e.clientX - rowLeftEdge))
}
const stopWidthResize = () => {
 // Capture phase (see startWidthResize) — removeEventListener must match.
 window.removeEventListener('mousemove', onWidthResize, true)
 window.removeEventListener('mouseup', stopWidthResize, true)
 document.body.style.userSelect = ''
 document.body.style.cursor = ''
 localStorage.setItem('teacherLeftColWidth', String(leftColWidth.value))
}
const startWidthResize = (e) => {
 e.preventDefault()  // stop native drag/text-selection from fighting the custom drag
 rowLeftEdge = rowEl.value?.getBoundingClientRect().left ?? 0
 document.body.style.userSelect = 'none'
 document.body.style.cursor = 'col-resize'
 // Capture phase, not bubble: with the coach-mark tour active, driver.js's dimmed
 // overlay sits on top of the page and stops propagation of its own mouse events —
 // if the cursor drifts over that overlay mid-drag (very likely, since dragging
 // moves the mouse away from the small highlighted grip), a bubble-phase listener
 // here never sees mouseup, leaving the drag "stuck" until an extra click releases
 // it — a click that then also lands on the overlay and closes the tour. Capture-
 // phase listeners on window fire before the overlay's own handling, regardless.
 window.addEventListener('mousemove', onWidthResize, true)
 window.addEventListener('mouseup', stopWidthResize, true)
}

// Same absolute-position approach as the width handle (target = row's bottom edge minus
// mouse Y — Stats sits at the bottom of the column now, so its height grows as the mouse
// moves up from the row's bottom edge), for the same dead-zone-avoidance reason.
let rowBottomEdge = 0

const onHeightResize = (e) => {
 e.preventDefault()
 const target = rowBottomEdge - e.clientY
 // Dragging well past the floor snaps to minimized; dragging back up expands again.
 statsCollapsed.value = target < MIN_STATS_HEIGHT / 2
 if (!statsCollapsed.value) statsHeight.value = Math.min(maxStatsHeight.value, Math.max(MIN_STATS_HEIGHT, target))
}
const stopHeightResize = () => {
 // Capture phase (see startHeightResize / startWidthResize) — removeEventListener must match.
 window.removeEventListener('mousemove', onHeightResize, true)
 window.removeEventListener('mouseup', stopHeightResize, true)
 document.body.style.userSelect = ''
 document.body.style.cursor = ''
 localStorage.setItem('teacherStatsHeight', String(statsHeight.value))
}
const startHeightResize = (e) => {
 e.preventDefault()  // stop native drag/text-selection from fighting the custom drag
 rowBottomEdge = rowEl.value?.getBoundingClientRect().bottom ?? 0
 document.body.style.userSelect = 'none'
 document.body.style.cursor = 'row-resize'
 window.addEventListener('mousemove', onHeightResize, true)
 window.addEventListener('mouseup', stopHeightResize, true)
}

// Measures the actual rendered height of the Stats+Calendar/Attendance row (not an
// approximation) so both columns can be sized to fill <main> exactly, down to its
// bottom edge, regardless of header height, etc.
const attachRowObserver = () => {
 if (!rowEl.value) return
 rowResizeObserver = new ResizeObserver((entries) => {
 const entry = entries[0]
 if (entry) rowHeight.value = entry.contentRect.height
 })
 rowResizeObserver.observe(rowEl.value)
}
const detachRowObserver = () => {
 if (rowResizeObserver) {
 rowResizeObserver.disconnect()
 rowResizeObserver = null
 }
}
watch(isWide, async (wide) => {
 detachRowObserver()
 if (wide) {
 await nextTick()
 attachRowObserver()
 }
}, { immediate: true })
// Crossing the mobile/desktop breakpoint mid-tour points the tour at now-hidden
// elements — abort cleanly rather than leave a stuck spotlight/popover.
watch(isWide, abortIfSurfaceMismatch)

const hideExcusedLessons = ref(false)

// Sync functionality
const { cleanup: cleanupSync } = useSync()

const hasSignupsPermission = computed(() => {
 const perms = effectivePermissions.value
 return perms.includes('admin') || perms.includes('lesson_signups')
})

// Staff with signups access get the full admin signups list; teachers get their own
// (TeacherSignupsTab — only signups connected to them).
const showSignupsTab = computed(() => hasSignupsPermission.value || effectivePermissions.value.includes('teacher'))

// The wide layout hosts the workspace sidebar (Attendance, Invoices, Signups pinned); the
// narrow one has no room, so its callers fall back to popups. Signups is (re)added
// whenever the dashboard hosts it, in case it was closed on an admin page.
const workspace = useAdminSidebar()
const signupsTabProps = () => hasSignupsPermission.value ? { isAdmin: effectivePermissions.value.includes('admin') } : { teacher: true }
watch([isWide, showSignupsTab, hasSignupsPermission], ([wide, show]) => {
 if (!wide) return workspace.releaseHost('dashboard')
 const signups = workspace.tabs.value.find(t => t.type === 'signups')
 if (show) workspace.ensure('signups', signupsTabProps())
 else if (signups) {  // e.g. previewing someone without access — not closable via close() while pinned
 workspace.tabs.value = workspace.tabs.value.filter(t => t !== signups)
 if (workspace.activeKey.value === signups.key) workspace.activeKey.value = ATTENDANCE
 }
 workspace.claimHost('dashboard', { teacherName: () => previewName.value, showInvoices: () => effectivePermissions.value.includes('teacher') })
}, { immediate: true, flush: 'post' })
onUnmounted(() => workspace.releaseHost('dashboard'))

// Tab badges: start once the user is known; switching the previewed teacher refetches.
const { badges, start: startBadges, stop: stopBadges, refresh: refreshBadges } = useTeacherBadges()
let badgesStarted = false
watch(previewName, (name) => {
 if (!name) return
 if (badgesStarted) refreshBadges()
 else { badgesStarted = true; startBadges() }
}, { immediate: true })
onUnmounted(() => { if (badgesStarted) stopBadges() })

watch(showSignupsTab, (show) => {
 if (!show && activeTab.value === 'lesson-signups') router.replace('/')
})

// The digest's "Answer in Scheduling" link (/?tab=scheduling): the pinned sidebar tab when
// wide, the /signups page when narrow.
watch([() => route.query.tab, showSignupsTab], ([tab, show]) => {
 if (tab !== 'scheduling' || !show) return
 if (isWide.value) {
  workspace.open('signups', signupsTabProps())
  router.replace('/')
 } else router.replace('/signups')
}, { immediate: true, flush: 'post' })

// Global keyboard shortcuts (help overlay, search, new-lesson, calendar nav)
// live in AppRoot.vue, not here — this component (Dashboard) unmounts on
// every other route (admin panel, team calendar), so a listener registered
// here would go dead the moment you left the dashboard. AppRoot is the one
// component that's always mounted.

const mobileTabs = computed(() => {
 const b = badges.value
 const tabs = [{ id: 'month-stats', label: 'Invoices', icon: ChartBarIcon, path: '/stats', badge: b.invoices }]
 tabs.push({ id: 'students', label: 'Students', icon: UserGroupIcon, path: '/students' })
 tabs.push({ id: 'my-schedule', label: 'Calendar', icon: CalendarIcon, path: '/my-schedule' })
 if (showSignupsTab.value) tabs.push({ id: 'lesson-signups', label: hasSignupsPermission.value ? 'Signups' : 'Scheduling', icon: UserPlusIcon, path: '/signups', badge: hasSignupsPermission.value ? 0 : b.signups })
 tabs.push({ id: 'attendance', label: 'Attendance', icon: CalendarDaysIcon, path: '/', badge: b.attendance })
 return tabs
})

// Load current user from localStorage
const loadCurrentUser = () => {
 try {
 const userData = localStorage.getItem('user')
 if (userData) {
 currentUser.value = JSON.parse(userData)
 }
 } catch (error) {
 console.error('Failed to load user from localStorage:', error)
 currentUser.value = null
 }
}

// Check authentication status with backend
const checkAuthStatus = async () => {
 try {
 const response = await fetch('/api/auth/me')
 if (response.ok) {
 const data = await response.json()
 currentUser.value = data
 localStorage.setItem('user', JSON.stringify(data))
 return true
 } else {
 // Not authenticated
 currentUser.value = null
 workspace.closeAll()
 localStorage.removeItem('user')
 return false
 }
 } catch (error) {
 console.error('Failed to check auth status:', error)
 currentUser.value = null
 workspace.closeAll()
 localStorage.removeItem('user')
 return false
 }
}

const loadPreferences = async () => {
 try {
 const data = await fetchApi('/api/teacher-availability/me')
 hideExcusedLessons.value = !!data.hide_excused_lessons
 } catch (error) {
 console.error('Failed to load preferences:', error)
 }
}

// First-run coach-mark tour, auto-triggered per-surface. `includeUniversal` is true
// only the very first time either surface's tour has ever run for this user — see
// .claude/plans/jazzy-hopping-stream.md for why that's intentional, not a gap.
// Skipped while the password-setup overlay is up (see SetPasswordModal below) —
// firing a spotlight tour behind/alongside that modal would fight it for attention.
const maybeStartFirstRunTour = () => {
 if (currentUser.value?.needs_password) return
 // Admins get their own tour eventually — the normal coachmarks tour (calendar,
 // availability, account steps aimed at a teacher's day-to-day) doesn't fit their
 // workflow, so skip it entirely rather than show a tour built for someone else's role.
 if (effectivePermissions.value.includes('admin')) return
 const surface = isWide.value ? 'desktop' : 'mobile'
 const alreadyDone = surface === 'desktop'
 ? currentUser.value.tour_desktop_completed_at
 : currentUser.value.tour_mobile_completed_at
 if (alreadyDone) {
 // Already finished the tour on this surface — rather than replaying the whole
 // thing, only surface a SHORT dedicated tour of what's new in TOUR_VERSION (see
 // whatsNewV3Steps in tourSteps.js), and only if they haven't seen it yet.
 // useTour.js persists the seen version to localStorage only on genuine
 // completion (Done).
 const seenVersion = Number(localStorage.getItem('tour_version_seen') || 0)
 if (seenVersion >= TOUR_VERSION) return
 startTour(surface, { whatsNew: true, firstName: previewName.value?.split(' ')[0] })
 return
 }
 const includeUniversal = !currentUser.value.tour_mobile_completed_at && !currentUser.value.tour_desktop_completed_at
 startTour(surface, { includeUniversal, firstName: previewName.value?.split(' ')[0] })
}

const startTourManually = () => {
 startTour(isWide.value ? 'desktop' : 'mobile', { includeUniversal: true, firstName: previewName.value?.split(' ')[0] })
}

// Bridges useTour.js (which can't reach these local refs directly) to the desktop
// pane switching used by the left-pane Stats tabs and the Attendance/Signups toggle.
const handleTourSetPane = (e) => {
 if (e.detail.statsPaneTab) { statsPaneTab.value = e.detail.statsPaneTab; statsCollapsed.value = false }
 if (e.detail.sidebarTab === 'attendance') workspace.activeKey.value = ATTENDANCE
 else if (e.detail.sidebarTab === 'signups') workspace.open('signups', signupsTabProps())
}

// Handle logout
const handleLogout = async () => {
 try {
 await fetch('/api/auth/logout', {
 method: 'POST'
 })
 } catch (error) {
 console.error('Logout API error:', error)
 } finally {
 // Clear local state regardless of API response
 currentUser.value = null
 workspace.closeAll()
 localStorage.removeItem('user')
 router.push('/login')
 }
}

onUnmounted(() => {
 window.removeEventListener('tour:set-pane', handleTourSetPane)
 stopWidthResize()
 stopHeightResize()
 detachRowObserver()
 cleanupSync()
 releaseViewport()
})

// Initialize authentication
onMounted(async () => {
 // Load user from localStorage first for immediate UI
 loadCurrentUser()

 // Then verify with backend
 const isAuthenticated = await checkAuthStatus()

 if (!isAuthenticated) {
 router.push('/login')
 return
 }

 window.addEventListener('tour:set-pane', handleTourSetPane)

 loadPreferences()
 maybeStartFirstRunTour()
})

// Watch for authentication changes
watch(currentUser, (newUser) => {
 if (!newUser) {
 // User logged out, redirect to login
 router.push('/login')
 }
})
</script>

<style scoped>
.tour-nav-pulse {
 animation: tour-pulse 0.6s ease-out;
}
@keyframes tour-pulse {
 0%, 100% { transform: scale(1); }
 30% { transform: scale(1.25); }
}
</style>
